import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Table, Tag } from "@arco-design/web-react";
import { Building2, ChevronDown, ChevronRight } from "lucide-react";
import type { ConfirmationSettlementGroup } from "../../service/confirmationSettlementService";
import type { FulfillmentOrderChangeBatch, FulfillmentOrderLine } from "../../types";
import { formatConfirmationAmount } from "../../service/confirmationDecimalDisplay";
import { confirmationTravelerRoleLabel } from "../../service/fulfillmentOrderEditorService";
import { getSafeFulfillmentOrderActionErrorMessage } from "../../service";
import { ConfirmationButton } from "./primitives";
import { buildFulfillmentLineAdjustments, FulfillmentOrderLineAdjustmentsPanel } from "../FulfillmentOrderLineAdjustmentsPanel";
import { FulfillmentOrderAgreementPreviewDrawer, type FulfillmentOrderAgreementPreviewProps } from "../FulfillmentOrderAgreementPreviewDrawer";
import "./settlementGroups.css";

/** 主体集合只接收既有聚合结果，写入与详情继续由原宿主流程负责。 */
export type ConfirmationSettlementGroupsProps = {
  groups: readonly ConfirmationSettlementGroup[];
  ungroupedLines: readonly FulfillmentOrderLine[];
  busy?: boolean;
  readOnly?: boolean;
  unapplied?: boolean;
  adjustmentChanges?: FulfillmentOrderChangeBatch[];
  currentVersionId?: string | null;
  adjustmentLoading?: boolean;
  adjustmentError?: unknown;
  onRetryAdjustments?: () => void;
  onGenerate: (supplierId: string, pendingLineIds: string[]) => Promise<unknown>;
  onSelectOrder: (orderId: string | null) => void;
  agreement: Omit<FulfillmentOrderAgreementPreviewProps, "visible" | "onClose" | "title" | "summary" | "children" | "footer" | "wrapClassName"> & { selectedOrderId: string | null };
  feedback?: (close: () => void) => ReactNode;
};

type ActiveCollection = { kind: "supplier"; id: string } | { kind: "ungrouped" };

/** 主体集合沿用宿主写入权限和已有协议状态，不扩大可生成范围。 */
function generationBlockReason(group: ConfirmationSettlementGroup | undefined, props: ConfirmationSettlementGroupsProps): string {
  if (!group) return "当前结算主体费用已变化，请重新查看。";
  if (props.readOnly) return "当前账号无生成协议权限。";
  if (props.busy) return "已有确认件操作正在处理，请稍候。";
  if (props.unapplied) return "请先应用或重置尚未应用的编辑内容。";
  if (group.orders.some(order => order.status !== "draft")) return "该主体已有生效或锁定协议，不能继续追加费用；可查看原协议或核对待生成费用的结算主体。";
  if (!group.pendingLineIds.length) return "本组没有待生成协议的费用。";
  return "";
}

/** 展示确认行已有服务日期，不从计价次数推导日期。 */
function lineDate(line: FulfillmentOrderLine): string {
  if (line.serviceDates?.length) return line.serviceDates.join("、");
  return [...new Set([line.serviceDateFrom, line.serviceDateTo].filter(Boolean))].join(" 至 ") || "未填写服务日期";
}

/** 按已有结算主体查看费用，并通过宿主原有入口生成和查看协议。 */
export function ConfirmationSettlementGroups(props: ConfirmationSettlementGroupsProps) {
  const { groups, ungroupedLines, onGenerate, onSelectOrder, agreement, feedback } = props;
  const [active, setActive] = useState<ActiveCollection | null>(null);
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const submittingRef = useRef(false);
  const mounted = useRef(true);
  const reasonId = useId();
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // 只记主体身份，费用、金额和待生成范围始终读取最新聚合结果。
  const group = active?.kind === "supplier" ? groups.find(item => item.settlementSupplierId === active.id) : undefined;
  const name = active?.kind === "ungrouped" ? "待核对结算主体" : group?.settlementSupplierName || "结算主体";
  const selectedOrder = group?.orders.find(order => order.id === agreement.selectedOrderId) ?? group?.orders[0];
  const selectedOrderId = selectedOrder?.id ?? null;
  const lines = active?.kind === "ungrouped" ? ungroupedLines : (group?.lines || []).filter(line => !line.fulfillmentOrderId || line.fulfillmentOrderId === selectedOrderId);
  // 生成回填后仍留在原抽屉；只有当前主体的精确协议详情才能用于展示和输出。
  useEffect(() => {
    if (active && selectedOrderId !== agreement.selectedOrderId) onSelectOrder(selectedOrderId);
  }, [active, selectedOrderId, agreement.selectedOrderId, onSelectOrder]);
  const detail = selectedOrderId && agreement.detail?.order.id === selectedOrderId
    && agreement.detail.order.settlementSupplierId === group?.settlementSupplierId ? agreement.detail : null;
  const detailError = agreement.selectedOrderId === selectedOrderId ? agreement.error : undefined;
  const detailLoading = Boolean(selectedOrderId && (agreement.loading || (!detail && !detailError)));
  const versionChanges = (props.adjustmentChanges ?? []).filter(batch => (
    !props.currentVersionId || !batch.baseVersionId || batch.baseVersionId === props.currentVersionId
  ));
  const blockedReason = generationBlockReason(group, props);
  const close = () => {
    if (submittingRef.current) return;
    setActive(null);
    setExpandedLineId(null);
    setError("");
    onSelectOrder(null);
  };
  const open = (collection: ActiveCollection) => {
    if (submittingRef.current) return;
    setError("");
    setExpandedLineId(null);
    setActive(collection);
  };
  const generate = async () => {
    if (submittingRef.current || blockedReason || !group) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError("");
    try {
      await onGenerate(group.settlementSupplierId, [...group.pendingLineIds]);
    } catch (cause) {
      if (mounted.current) setError(getSafeFulfillmentOrderActionErrorMessage(cause, "生成协议失败，请稍后重试"));
    } finally {
      submittingRef.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  return <>
    <details className="confirmation-sidebar-panel confirmation-settlement-groups" open>
      <summary><h3>结算主体费用</h3><span className="confirmation-settlement-count">{groups.length} 个主体</span></summary>
      <div className="confirmation-settlement-group-list">
        {groups.map(item => <button key={item.settlementSupplierId} type="button" className="confirmation-settlement-group-card" aria-label={`查看 ${item.settlementSupplierName} 的费用`} disabled={submitting} onClick={() => open({ kind: "supplier", id: item.settlementSupplierId })}>
          <span className="confirmation-settlement-group-heading"><Building2 aria-hidden /><strong>{item.settlementSupplierName}</strong><ChevronRight aria-hidden /></span>
          <span className="confirmation-settlement-group-amount">{formatConfirmationAmount(item.amount)}</span>
          <span className="confirmation-settlement-group-meta"><span>{item.lines.length} 项费用</span><span className="confirmation-settlement-status" data-pending={Boolean(item.pendingLineIds.length)}>{item.pendingLineIds.length ? `${item.pendingLineIds.length} 项待生成协议` : `${item.orders.length} 份协议`}</span></span>
        </button>)}
        {ungroupedLines.length ? <button type="button" className="confirmation-settlement-group-card confirmation-settlement-group-unassigned" aria-label="查看 待核对结算主体 的费用" disabled={submitting} onClick={() => open({ kind: "ungrouped" })}>
          <strong>待核对结算主体</strong><span>{ungroupedLines.length} 项费用</span>
        </button> : null}
        {!groups.length && !ungroupedLines.length ? <p>尚无活动确认费用。</p> : null}
      </div>
    </details>
    <FulfillmentOrderAgreementPreviewDrawer
      {...agreement}
      detail={detail}
      loading={detailLoading}
      error={detailError}
      visible={active !== null}
      onClose={close}
      title={`${name} · 合同与费用`}
      actionPending={submitting || props.busy || agreement.actionPending}
      wrapClassName="confirmation-editor-theme confirmation-settlement-groups-drawer"
      summary={group ? <div className="confirmation-settlement-summary">
        <span>{group.lines.length} 项费用 · {group.orders.length} 份协议</span>
        <strong aria-label="当前费用合计">当前费用合计 {formatConfirmationAmount(group.amount)}</strong>
        {selectedOrder ? <span className="confirmation-settlement-protocol-meta"><span>{selectedOrder.orderNo}</span><Tag size="small" color={selectedOrder.status === "effective" ? "green" : selectedOrder.status === "draft" ? "orange" : "gray"}>{selectedOrder.status === "draft" ? "待盖章" : selectedOrder.status === "effective" ? "已生效" : "已锁定"}</Tag></span> : <span>核对以下费用后生成确认协议</span>}
        {group.orders.length > 1 ? <div className="confirmation-settlement-order-switch" aria-label="本主体协议">{group.orders.map(order => <ConfirmationButton key={order.id} size="sm" aria-pressed={order.id === selectedOrderId} disabled={submitting || agreement.actionPending} onClick={() => { setExpandedLineId(null); onSelectOrder(order.id); }}>{order.orderNo}</ConfirmationButton>)}</div> : null}
      </div> : <p>核对费用的结算主体与现有归口关系后，再生成确认协议。</p>}
      footer={!selectedOrder || Boolean(group?.pendingLineIds.length) ? <div className="confirmation-settlement-generation"><div className="confirmation-settlement-footer-note">{group ? <><span>待生成协议 {group.pendingLineIds.length} 项 · {formatConfirmationAmount(group.pendingAmount)}</span>{blockedReason ? <p id={reasonId}>{blockedReason}</p> : null}</> : <span>未确认结算主体的费用暂不能生成协议。</span>}</div>{active?.kind === "supplier" ? <ConfirmationButton variant="default" loading={submitting} disabled={Boolean(blockedReason) || agreement.actionPending} aria-describedby={blockedReason ? reasonId : undefined} onClick={() => void generate()}>生成确认协议</ConfirmationButton> : null}</div> : null}>
      <div className="confirmation-settlement-collection">
        <section className="confirmation-settlement-lines crm-list-panel" aria-label="本主体费用明细">
          <div className="confirmation-settlement-section-heading fulfillment-order-contract-section-heading crm-list-section-header"><h2>费用明细</h2><span>共 {lines.length} 项费用</span></div>
          {props.adjustmentError && !props.adjustmentLoading ? <div className="confirmation-settlement-history-error" role="alert"><span>增减记录加载失败</span>{props.onRetryAdjustments ? <ConfirmationButton variant="ghost" size="sm" onClick={props.onRetryAdjustments}>重试增减记录</ConfirmationButton> : null}</div> : null}
          <Table<FulfillmentOrderLine>
            rowKey="id"
            data={[...lines]}
            border={false}
            pagination={false}
            size="small"
            scroll={{ x: 780 }}
            noDataElement="当前集合没有活动费用，请关闭后查看最新主体集合。"
            expandedRowKeys={expandedLineId && lines.some(line => line.id === expandedLineId) ? [expandedLineId] : []}
            expandProps={{ icon: () => null, width: 0 }}
            expandedRowRender={line => <FulfillmentOrderLineAdjustmentsPanel layout="cards" line={line} detail={null} changes={props.adjustmentChanges} currentVersionId={props.currentVersionId} loading={Boolean(props.adjustmentLoading)} error={props.adjustmentError} onRetry={() => props.onRetryAdjustments?.()} formatAmount={formatConfirmationAmount} />}
            columns={[
              { title: "费用项目", dataIndex: "itemNameSnapshot", width: 280, render: (_, line) => <div className="confirmation-settlement-line-title"><strong>{line.itemNameSnapshot}</strong><small>第 {line.lineNo} 行 · {line.resourceType}{line.travelerRole ? ` · ${confirmationTravelerRoleLabel(line.travelerRole)}` : ""}</small>{line.itemSpecSnapshot ? <span>{line.itemSpecSnapshot}</span> : null}</div> },
              { title: "服务日期", dataIndex: "serviceDates", width: 170, render: (_, line) => <span className="confirmation-settlement-line-date">{lineDate(line)}</span> },
              { title: "原确认金额", dataIndex: "amount", width: 150, align: "right", render: (_, line) => <div className="confirmation-settlement-line-amount"><strong>{formatConfirmationAmount(line.amount)}</strong><small className="confirmation-settlement-status" data-pending={!line.fulfillmentOrderId}>{line.fulfillmentOrderId ? "已归口" : "待生成协议"}</small></div> },
              { title: "增减记录", key: "adjustments", width: 180, align: "center", render: (_, line) => {
                if (props.adjustmentLoading) return <span className="confirmation-settlement-empty">加载中…</span>;
                if (props.adjustmentError) return <span className="confirmation-settlement-empty">暂无法获取</span>;
                const count = buildFulfillmentLineAdjustments(line, null, versionChanges).length;
                return count ? <ConfirmationButton variant="ghost" size="sm" className="confirmation-settlement-history-link" disabled={submitting} aria-label={`查看 ${line.itemNameSnapshot} 的增减记录`} aria-expanded={expandedLineId === line.id} onClick={() => setExpandedLineId(current => current === line.id ? null : line.id)}>{expandedLineId === line.id ? "收起记录" : "查看记录"}（{count} 条）<ChevronDown aria-hidden /></ConfirmationButton> : <span className="confirmation-settlement-empty">暂无增减记录</span>;
              } },
            ]}
          />
        </section>
        {error ? <p className="confirmation-settlement-submit-error" role="alert">{error}</p> : null}
        {active?.kind === "supplier" ? feedback?.(close) : null}
      </div>
    </FulfillmentOrderAgreementPreviewDrawer>
  </>;
}
